import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { ApplicationCapabilityVisibilityRow, CapabilityVisibility } from '@/types/capability-types';

const queryKey = (appKey: string) => ['app-capability-visibility', appKey] as const;

export function useAppCapabilityVisibility(appKey: string) {
    return useQuery<ApplicationCapabilityVisibilityRow[]>({
        queryKey: queryKey(appKey),
        queryFn: () =>
            api
                .get(`/applications/${appKey}/capability-visibility`)
                .then((r) => r.data?.data ?? []),
        enabled: !!appKey,
    });
}

export function useUpdateAppCapabilityVisibility(appKey: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (overrides: { capability_id: string; visibility: CapabilityVisibility }[]) =>
            api
                .put(`/applications/${appKey}/capability-visibility`, { overrides })
                .then((r) => r.data?.data ?? []),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKey(appKey) });
        },
    });
}

export function useDeleteAppCapabilityVisibility(appKey: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (capabilityId: string) =>
            api.delete(`/applications/${appKey}/capability-visibility/${capabilityId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKey(appKey) });
        },
    });
}
